import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChevronUp, ChevronDown, Share, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import html2canvas from "html2canvas";

interface Submission {
  id: string;
  content: string;
  author: string;
}

interface ReviewSectionProps {
  userSubmission: string;
  category: string;
  onComplete: () => void;
  generatedImageUrl: string | null;
}

export function ReviewSection({
  userSubmission,
  category,
  onComplete,
  generatedImageUrl,
}: ReviewSectionProps) {
  const [submissions, setSubmissions] = useState<Submission[]>([
    {
      id: "1",
      content:
        "AI is revolutionizing creative work, enabling artists and writers to explore new ideas faster than ever before. Yet the human touch remains essential for truly meaningful content. The future will belong to those who master the hybrid approach of AI assistance with human direction and refinement.",
      author: "CreativeMind22",
    },
    {
      id: "2",
      content:
        "The most exciting aspect of AI-assisted creativity isn't that it makes humans obsolete—it's that it amplifies our uniquely human abilities. When routine tasks are automated, we're free to focus on the conceptual, emotional work that machines can't replicate. This symbiosis will be the cornerstone of a new creative renaissance.",
      author: "FutureThinker",
    },
    {
      id: "3",
      content:
        "As AI tools become more sophisticated, the distinction between human and machine-generated content grows blurrier. The question we should be asking isn't 'can AI create art?' but rather 'how does AI change our relationship with creativity?' The future belongs to those who view AI as a collaborator rather than a competitor.",
      author: "DigitalArtisan",
    },
  ]);

  const submissionRef = useRef<HTMLDivElement>(null);
  const [localImage, setLocalImage] = useState<string | null>(null);
  const [paperBackground, setPaperBackground] = useState<string | null>(null);
  const [isGeneratingLocal, setIsGeneratingLocal] = useState(true);
  const [isLoadingRemoteImage, setIsLoadingRemoteImage] = useState(
    !generatedImageUrl
  );

  // Load paper background
  useEffect(() => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = 500;
    canvas.height = 500;

    if (ctx) {
      // Create a gradient background that looks like paper
      const gradient = ctx.createLinearGradient(0, 0, 0, 500);
      gradient.addColorStop(0, "#f5f5f5");
      gradient.addColorStop(1, "#e8e8e8");

      // Fill background
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 500, 500);

      // Add some noise/texture
      for (let i = 0; i < 50000; i++) {
        const x = Math.random() * 500;
        const y = Math.random() * 500;
        const opacity = Math.random() * 0.03;
        ctx.fillStyle = `rgba(0, 0, 0, ${opacity})`;
        ctx.fillRect(x, y, 1, 1);
      }

      setPaperBackground(canvas.toDataURL());
    }
  }, []);

  // Generate local image immediately
  useEffect(() => {
    const generateLocalImage = async () => {
      if (!submissionRef.current || !paperBackground) return;

      try {
        const canvas = await html2canvas(submissionRef.current, {
          backgroundColor: "transparent",
          width: 500,
          height: 500,
          scale: 2,
          useCORS: true,
          logging: true,
        });

        const finalCanvas = document.createElement("canvas");
        const ctx = finalCanvas.getContext("2d");
        finalCanvas.width = canvas.width;
        finalCanvas.height = canvas.height;

        if (ctx) {
          // Load paper background
          const bgImage = new Image();
          bgImage.onload = () => {
            ctx.drawImage(bgImage, 0, 0, finalCanvas.width, finalCanvas.height);
            ctx.drawImage(canvas, 0, 0);
            setLocalImage(finalCanvas.toDataURL());
            setIsGeneratingLocal(false);
          };
          bgImage.src = paperBackground;
        }
      } catch (error) {
        console.error("Error generating local image:", error);
        setIsGeneratingLocal(false);
      }
    };

    generateLocalImage();
  }, [userSubmission, paperBackground]);

  // Handle transition to API-generated image
  useEffect(() => {
    if (!generatedImageUrl) {
      setIsLoadingRemoteImage(true);
      return;
    }

    setIsLoadingRemoteImage(true);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setIsLoadingRemoteImage(false);
      setIsGeneratingLocal(false);
    };
    img.onerror = () => {
      setIsLoadingRemoteImage(false);
    };
    img.src = generatedImageUrl;
  }, [generatedImageUrl]);

  const getCategoryDisplayName = () => {
    switch (category) {
      case "tweet":
        return "tweeter";
      case "marketing":
        return "copywriter";
      case "poetry":
        return "poet";
      default:
        return "writer";
    }
  };

  const moveSubmission = (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === submissions.length - 1)
    ) {
      return;
    }

    const newIndex = direction === "up" ? index - 1 : index + 1;
    const newSubmissions = [...submissions];
    [newSubmissions[index], newSubmissions[newIndex]] = [
      newSubmissions[newIndex],
      newSubmissions[index],
    ];
    setSubmissions(newSubmissions);
  };

  const handleShareToTwitter = () => {
    // Remove <p> tags and clean up the text
    const cleanText = userSubmission
      .replace(/<p>/g, "")
      .replace(/<\/p>/g, "\n");
    const tweetText = encodeURIComponent(cleanText.substring(0, 280));
    const websiteUrl = encodeURIComponent("https://vibewriter.com");
    const imageUrl = encodeURIComponent(generatedImageUrl || localImage || "");
    const twitterUrl = `https://twitter.com/intent/tweet?text=${tweetText}&url=${websiteUrl}${
      imageUrl ? "&image=" + imageUrl : ""
    }`;
    window.open(twitterUrl, "_blank");
  };

  return (
    <div className="space-y-8">
      <div className="glass-card bg-gradient-to-r from-primary/20 to-secondary/20 p-6 rounded-lg">
        <h3 className="text-xl font-bold mb-2 font-heading">Your Submission</h3>
        <div className="text-white/80 mb-4">
          <div className="mb-3 whitespace-pre-wrap">
            <div dangerouslySetInnerHTML={{ __html: userSubmission }} />
          </div>
        </div>

        {/* Hidden div for rendering */}
        <div
          ref={submissionRef}
          style={{
            width: "500px",
            height: "500px",
            padding: "40px",
            backgroundColor: "transparent",
            color: "#1a1a1a",
            fontFamily: "Inter, sans-serif",
            fontSize: "24px",
            lineHeight: "1.6",
            position: "absolute",
            left: "-9999px",
            whiteSpace: "pre-wrap",
            display: "block",
            overflow: "hidden",
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: userSubmission }} />
          <div className="absolute bottom-5 right-5 text-lg text-black/50">
            vibeanything.ai
          </div>
        </div>

        {/* Display image with loading state */}
        <div className="mb-4">
          <img
            src={generatedImageUrl || localImage}
            alt="Rendered submission"
            className={cn(
              "w-full max-w-[300px] rounded-lg shadow-lg mx-auto transition-opacity duration-300",
              isGeneratingLocal ? "opacity-50" : "opacity-100"
            )}
            style={{
              boxShadow:
                "0 4px 14px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)",
            }}
          />
        </div>

        <Button
          onClick={handleShareToTwitter}
          className="bg-[#1DA1F2] hover:bg-[#1A91DA] text-white"
          disabled={isLoadingRemoteImage}
        >
          {isLoadingRemoteImage ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Share className="mr-2 h-4 w-4" />
          )}
          Share to Twitter
        </Button>
      </div>

      <div>
        <div className="glass-card p-6 rounded-lg mb-6">
          <h3 className="text-xl font-bold mb-2 font-heading">
            Rate Other Submissions
          </h3>
          <p className="text-white/80 mb-4">
            Part of being the best {getCategoryDisplayName()} is having good
            taste. Rank these submissions from best to worst using the arrows.
            Your score will be based on how well your rankings match the
            community's preferences.
          </p>
        </div>

        <div className="space-y-4">
          {submissions.map((submission, index) => (
            <Card
              key={submission.id}
              className="glass-card relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 flex flex-col p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={index === 0}
                  onClick={() => moveSubmission(index, "up")}
                  className={cn(
                    "h-8 w-8 p-0 rounded-full",
                    index === 0 ? "opacity-50" : "opacity-100"
                  )}
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={index === submissions.length - 1}
                  onClick={() => moveSubmission(index, "down")}
                  className={cn(
                    "h-8 w-8 p-0 rounded-full",
                    index === submissions.length - 1
                      ? "opacity-50"
                      : "opacity-100"
                  )}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>

              <div className="p-5 pl-14">
                <div className="text-sm font-semibold text-primary mb-2">
                  Rank #{index + 1}
                </div>
                <p className="text-white/90 mb-2">{submission.content}</p>
                <div className="text-xs text-white/50 text-right">
                  By: {submission.author}
                </div>
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-8 flex justify-center">
          <Button
            onClick={onComplete}
            className="bg-gradient-to-r from-primary to-secondary text-white px-8"
          >
            Submit Rankings
          </Button>
        </div>
      </div>
    </div>
  );
}
